(() => {
  'use strict';
  if (customElements.get('haven-welcome')) return;
  customElements.define('haven-welcome', class extends HTMLElement {
    connectedCallback() {
      clearTimeout(this.boot);
      this.boot = setTimeout(() => { if (this.isConnected) this.init(); }, 0);
    }
    init() {
      if (this.mounted) return;
      this.mounted = true;
      this.abort = new AbortController();
      const on = (element, type, handler, options = {}) => element.addEventListener(type, handler, { ...options, signal: this.abort.signal });
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.teaser = this.querySelector('[data-hw-open]');
      if (!this.dialog || !this.form || typeof this.dialog.showModal !== 'function') return;
      this.key = 'haven-welcome-v1';
      this.ready = false;
      this.seen = false;
      this.lastAction = Date.now();
      this.home = this.dataset.home === 'true';
      this.retryDelay = this.home ? 120 : 1800;
      this.editor = this.dataset.editor === 'true';
      this.testing = this.editor && this.dataset.test === 'true';
      this.subscribed = this.dataset.subscribed === 'true' || this.read('subscribed') === '1';
      this.suppress = !this.home && (Number(this.read('dismissed')) > Date.now() || this.session('seen') === '1');
      const success = this.querySelector('[data-hw-success]');
      const errors = this.querySelector('[data-hw-errors]');
      const pending = Number(this.session('pending')) > Date.now() - 600000;
      const forThisForm = location.hash === '#' + this.form.id || pending;
      this.response = Boolean((success || errors) && forThisForm);
      this.responsePending = this.response || this.testing;
      if (success && forThisForm) {
        this.subscribed = true;
        this.write('subscribed', '1');
        this.session('pending', null);
      }
      on(this.teaser, 'click', () => this.open(true));
      this.querySelectorAll('[data-hw-close]').forEach(button => on(button, 'click', () => this.close()));
      on(this.dialog, 'cancel', event => { event.preventDefault(); this.close(); });
      on(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      });
      on(this.dialog, 'close', () => this.afterClose());
      on(this.form, 'submit', () => {
        if (this.form.checkValidity()) this.session('pending', String(Date.now()));
      });
      on(this.dialog, 'keydown', event => {
        if (event.key !== 'Tab') return;
        const controls = [...this.dialog.querySelectorAll('a[href],button,input,select,textarea,[tabindex="0"]')].filter(element => !element.disabled && element.getClientRects().length);
        if (!controls.length) return;
        const first = controls[0];
        const last = controls.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus();
        }
      });
      const activity = () => {
        this.lastAction = Date.now();
        if (!this.seen && !this.suppress && !this.subscribed) this.scheduleCheck(this.retryDelay);
      };
      on(document, 'pointerdown', activity, { passive: true });
      on(document, 'keydown', activity, { passive: true });
      on(document, 'scroll', activity, { passive: true });
      on(document, 'click', event => {
        const trigger = event.target.closest?.('[data-hw-trigger]');
        if (trigger) this.open(true);
        this.scheduleCheck(this.retryDelay);
      }, { passive: true });
      on(document, 'focusout', () => this.scheduleCheck(this.retryDelay));
      on(document, 'visibilitychange', () => {
        this.lastAction = Date.now();
        this.scheduleCheck(this.retryDelay);
      });
      on(document, 'haven:overlay', () => this.scheduleCheck(this.retryDelay));
      on(document, 'close', () => this.scheduleCheck(this.retryDelay), { capture: true });
      on(document, 'shopify:section:load', () => this.updateTriggers());
      on(document, 'shopify:section:select', event => {
        if (this.editor && (event.detail?.sectionId === this.dataset.sectionId || event.target.contains(this))) this.open(true);
      });
      this.observer = new MutationObserver(() => this.scheduleCheck(this.retryDelay));
      this.observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      if (this.responsePending) {
        this.ready = true;
        this.timer = setTimeout(() => this.maybeOpen(), 200);
      } else if (!this.editor) {
        this.timer = setTimeout(() => {
          this.ready = true;
          this.updateTeaser();
          this.maybeOpen();
        }, Math.max(1, Number(this.dataset.delay) || 1) * 1000);
      }
      this.updateTriggers();
      this.updateTeaser();
    }
    scheduleCheck(delay = this.retryDelay) {
      clearTimeout(this.recheckTimer);
      this.recheckTimer = setTimeout(() => {
        if (!this.mounted) return;
        this.updateTeaser();
        this.maybeOpen();
      }, delay);
    }
    read(key) {
      try { return localStorage.getItem(this.key + '-' + key); } catch (_) { return null; }
    }
    write(key, value) {
      try { localStorage.setItem(this.key + '-' + key, value); } catch (_) {}
    }
    session(key, value) {
      try {
        if (value === null) { sessionStorage.removeItem(this.key + '-' + key); return null; }
        if (value !== undefined) sessionStorage.setItem(this.key + '-' + key, value);
        return sessionStorage.getItem(this.key + '-' + key);
      } catch (_) { return null; }
    }
    blocked() {
      return document.hidden || document.body.classList.contains('overflow-hidden') || Boolean(document.querySelector('dialog[open]:not(.hw-dialog)')) || Boolean(document.querySelector('iframe[src*="hcaptcha"]:not([aria-hidden="true"])') && location.pathname.includes('/challenge'));
    }
    maybeOpen() {
      if (!this.mounted || this.dialog.open || !this.ready || this.blocked()) return;
      if (this.responsePending) { this.open(true); return; }
      if (this.subscribed || this.suppress || this.seen || this.editor) return;
      if (this.dataset.homeOnly === 'true' && this.dataset.home !== 'true') return;
      if (document.activeElement?.matches('input,textarea,select,[contenteditable="true"]')) return;
      if (!this.home) {
        if (document.querySelector('.uh-light-traveling,.uh-dragging,.uh-look-active')) return;
        const idle = Date.now() - this.lastAction;
        if (idle < 1800) { this.scheduleCheck(1800 - idle); return; }
      }
      this.open(false);
    }
    open(manual = false) {
      if (!this.mounted || this.dialog.open || this.blocked() || (!manual && this.subscribed)) return;
      this.returnFocus = document.activeElement;
      this.dialog.showModal();
      this.responsePending = false;
      this.seen = true;
      this.session('seen', '1');
      this.oldOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.dataset.havenDialogOpen = 'true';
      document.dispatchEvent(new CustomEvent('haven:overlay', { detail: { open: true } }));
      this.updateTeaser();
      const success = this.querySelector('[data-hw-success]');
      if (success && this.response) success.focus();
      else this.querySelector('.hw-close').focus();
    }
    close() {
      if (this.dialog.open) this.dialog.close();
    }
    restorePage() {
      document.documentElement.style.overflow = this.oldOverflow || '';
      delete document.documentElement.dataset.havenDialogOpen;
      document.dispatchEvent(new CustomEvent('haven:overlay', { detail: { open: false } }));
    }
    afterClose() {
      this.restorePage();
      if (!this.editor) {
        this.write('dismissed', String(Date.now() + (Number(this.dataset.dismissDays) || 7) * 86400000));
        this.suppress = true;
      }
      this.updateTeaser();
      if (this.returnFocus?.isConnected && this.returnFocus !== document.body) this.returnFocus.focus({ preventScroll: true });
    }
    updateTriggers() {
      document.querySelectorAll('[data-hw-trigger]').forEach(trigger => {
        trigger.hidden = this.subscribed;
        trigger.setAttribute('aria-controls', this.dialog.id);
      });
    }
    updateTeaser() {
      if (!this.teaser) return;
      const safePage = this.dataset.home === 'true' || location.pathname.includes('/collections');
      this.teaser.hidden = !(this.dataset.teaser === 'true' && !this.subscribed && !this.dialog.open && !this.blocked() && safePage && !this.editor);
    }
    disconnectedCallback() {
      clearTimeout(this.boot);
      clearTimeout(this.timer);
      clearTimeout(this.recheckTimer);
      this.abort?.abort();
      this.observer?.disconnect();
      if (this.dialog?.open) { this.dialog.close(); this.restorePage(); }
      document.querySelectorAll('[data-hw-trigger]').forEach(trigger => { trigger.hidden = true; });
      this.mounted = false;
    }
  });
})();
